<script lang="ts">
import type { UseComponentIconsProps } from '../composables/useComponentIcons'
import { useButtonGroup } from '../composables/useButtonGroup'
import { ui as textFieldUi } from './AwTextField.vue'

type TextareaValue = string | number | null

export interface TextareaProps extends UseComponentIconsProps {
  as?: string | Component
  id?: string
  name?: string
  placeholder?: string
  color?: 'default' | 'critical'
  size?: 'sm' | 'md' | 'lg'
  required?: boolean
  autofocus?: boolean
  autofocusDelay?: number
  autoresize?: boolean
  autoresizeDelay?: number
  disabled?: boolean
  rows?: number
  maxrows?: number
  modelModifiers?: {
    string?: boolean
    trim?: boolean
    lazy?: boolean
    nullify?: boolean
  }
  class?: string | object
}

export interface TextareaEmits<T extends TextareaValue = TextareaValue> {
  (e: 'update:modelValue', payload: T): void
  (e: 'blur', event: FocusEvent): void
  (e: 'change', event: Event): void
  (e: 'focus', event: FocusEvent): void
}

export interface TextareaSlots {
  leading?: (props?: object) => any
  trailing?: (props?: object) => any
}

const ui = {
  size: {
    sm: {
      leading: 'top-3! bottom-unset!',
      trailing: 'top-3! bottom-unset!',
    },
    md: {
      leading: 'top-3! bottom-unset!',
      trailing: 'top-3! bottom-unset!',
    },
    lg: {
      leading: 'top-3! bottom-unset!',
      trailing: 'top-3! bottom-unset!',
    },
  },
}
</script>

<script setup lang="ts" generic="T extends TextareaValue">
import { Primitive } from 'reka-ui'
import { type Component, computed, nextTick, onMounted, ref, watch } from 'vue'
import { useComponentIcons } from '../composables/useComponentIcons'
import { looseToNumber } from '../utils'
import AwIcon from './AwIcon.vue'

defineOptions({ inheritAttrs: false })

const props = withDefaults(defineProps<TextareaProps>(), {
  rows: 3,
  maxrows: 0,
  autofocusDelay: 0,
  autoresizeDelay: 0,
})
const emits = defineEmits<TextareaEmits<T>>()
const slots = defineSlots<TextareaSlots>()

const [modelValue, modelModifiers] = defineModel<T>()

const { hasButtonGroup, orientation, size: buttonGroupSize } = useButtonGroup<TextareaProps>(props)
const { isLeading, isTrailing, leadingIconName, trailingIconName } = useComponentIcons(props)

const textareaRef = ref<HTMLTextAreaElement | null>(null)

function updateInput(value: string | null) {
  if (modelModifiers.trim)
    value = value?.trim() ?? null
  if (modelModifiers.number)
    value = looseToNumber(value)
  if (modelModifiers.nullify)
    value ||= null
  modelValue.value = value as T
}
function onInput(event: Event) {
  autoResize()
  if (!modelModifiers.lazy)
    updateInput((event.target as HTMLInputElement).value)
}
function onChange(event: Event) {
  const value = (event.target as HTMLInputElement).value
  if (modelModifiers.lazy) {
    updateInput(value)
  }
  if (modelModifiers.trim) {
    (event.target as HTMLInputElement).value = value.trim()
  }
  emits('change', event)
}
function onBlur(event: FocusEvent) {
  emits('blur', event)
}
function autoFocus() {
  if (props.autofocus)
    textareaRef.value?.focus()
}
function onFocus(event: FocusEvent) {
  emits('focus', event)
}
function autoResize() {
  if (props.autoresize && textareaRef.value) {
    textareaRef.value.rows = props.rows
    const overflow = textareaRef.value.style.overflow
    textareaRef.value.style.overflow = 'hidden'

    const styles = window.getComputedStyle(textareaRef.value)
    const paddingTop = Number.parseInt(styles.paddingTop)
    const paddingBottom = Number.parseInt(styles.paddingBottom)
    const padding = paddingTop + paddingBottom
    const lineHeight = Number.parseInt(styles.lineHeight)
    const { scrollHeight } = textareaRef.value
    const newRows = (scrollHeight - padding) / lineHeight

    if (newRows > props.rows) {
      textareaRef.value.rows = props.maxrows ? Math.min(newRows, props.maxrows) : newRows
    }

    textareaRef.value.style.overflow = overflow
  }
}

watch(modelValue, () => {
  nextTick(autoResize)
})

const size = computed(() => props.size ?? 'md')
const color = computed(() => props.color ?? 'default')
const rootClass = computed(() => [
  textFieldUi.root,
  props.class,
  hasButtonGroup.value && textFieldUi.buttonGroup[orientation.value].root,
])
const baseClass = computed(() => [
  textFieldUi.base,
  textFieldUi.color[color.value].base,
  textFieldUi.color[color.value].border,
  textFieldUi.size[buttonGroupSize.value ?? size.value].base,
  hasButtonGroup.value && textFieldUi.buttonGroup[orientation.value].base,
  isTrailing.value && 'pr-6.5',
  isLeading.value && 'pl-6.5',
])
const trailingClass = computed(() => [
  textFieldUi.trailing,
  textFieldUi.size[buttonGroupSize.value ?? size.value].trailing,
  ui.size[buttonGroupSize.value ?? size.value].trailing,
])
const trailingIconClass = computed(() => [
  textFieldUi.trailingIcon,
  textFieldUi.size[buttonGroupSize.value ?? size.value].trailingIcon,
])
const leadingClass = computed(() => [
  textFieldUi.leading,
  textFieldUi.size[buttonGroupSize.value ?? size.value].leading,
  props.isLoading && !isLeading.value && 'animate-spin',
  ui.size[buttonGroupSize.value ?? size.value].leading,
])
const leadingIconClass = computed(() => [
  textFieldUi.leadingIcon,
  textFieldUi.size[buttonGroupSize.value ?? size.value].leadingIcon,
  props.isLoading && 'animate-spin',
])

onMounted(() => {
  setTimeout(() => {
    autoFocus()
  }, props.autofocusDelay)
  setTimeout(() => {
    autoResize()
  }, props.autoresizeDelay)
})

defineExpose({ textareaRef })
</script>

<template>
  <Primitive :as="as" :class="rootClass">
    <textarea
      :id="id"
      ref="textareaRef"
      :value="modelValue"
      :name="name"
      :rows="rows"
      :placeholder="placeholder"
      :class="baseClass"
      :disabled
      :required
      v-bind="$attrs"
      @input="onInput"
      @blur="onBlur"
      @change="onChange"
      @focus="onFocus"
    />
    <span v-if="isLeading || !!slots.leading" :class="leadingClass">
      <slot name="leading">
        <AwIcon v-if="isLeading && leadingIconName" :name="leadingIconName" :class="leadingIconClass" />
      </slot>
    </span>

    <span v-if="isTrailing || !!slots.trailing" :class="trailingClass">
      <slot name="trailing">
        <AwIcon v-if="trailingIconName" :name="trailingIconName" :class="trailingIconClass" />
      </slot>
    </span>
  </Primitive>
</template>
